<template>
    <div
        :style="{
            'font-family': faq.fontFamily,
            'background-color': faq.transparentBackground ? 'transparent' : faq.faqColor,
        }"
        class="py-12 lg:py-24"
    >
        <div
            class="faq max-w-7xl mx-auto px-4 md:px-12"
            :class="[faq.layout === 'aside' ? 'lg:flex lg:items-start' : '']"
        >
            <div
                v-if="faq.title || faq.subtitle"
                class="text-center pb-12 lg:pb-16 flex flex-col items-center justify-center"
                :class="[
                    faq.layout === 'aside' ? 'lg:w-1/5 lg:mr-12 flex-shrink-0 lg:mt-12' : '',
                    faq.layout === 'aside' && faq.textAlign === 'right' ? 'lg:text-right' : '',
                    faq.layout === 'aside' && faq.textAlign === 'left' ? 'lg:text-left' : '',
                    faq.layout === 'aside' && faq.textAlign === 'center' ? 'lg:text-center' : '',
                ]"
            >
                <cUtilsEdit @edit="goToLayoutField('faqTitle')">
                    <h2
                        v-if="faq.title"
                        class="font-semibold"
                        :style="{
                            color: faq.titleColor,
                            'font-size': faq.titleSize + 'px',
                        }"
                    >
                        {{ faq.title }}
                    </h2>
                </cUtilsEdit>
                <div class="mt-4" v-if="faq.subtitle">
                    <cUtilsEdit @edit="goToLayoutField('faqSubtitle')">
                        <h3
                            class="font-regular opacity-80"
                            :style="{
                                color: faq.titleColor,
                                'font-size': faq.titleSize - 8 + 'px',
                            }"
                            v-html="faqSubtitle"
                            @click.prevent="checkPreview"
                        ></h3>
                    </cUtilsEdit>
                </div>
                <div class="mt-6" v-if="hasSocials">
                    <cUtilsEdit @edit="goToLayoutField('faqSocials')">
                        <SocialsList v-if="hasSocials" :faq="faq" />
                    </cUtilsEdit>
                </div>
            </div>
            <div
                :class="[
                    faq.withSections && faq.showNav && faq.layout !== 'slider'
                        ? 'flex-grow flex flex-col lg:flex-row gap-9'
                        : 'flex-grow',
                ]"
            >
                <div class="order-2 lg:order-1 w-full flex-grow">
                    <div
                        v-if="
                            !(faq.withSections && faq.showNav && faq.layout !== 'slider') &&
                            faq.showSearch
                        "
                        class="flex justify-end"
                    >
                        <div
                            class="w-full lg:max-w-sm relative mb-6"
                            :style="{
                                color: faq.titleColor,
                            }"
                        >
                            <input
                                :style="{
                                    'background-color': faq.faqColor,
                                    '--tw-ring-color': faq.borderColor,
                                    'border-radius': faq.borderRadius + 'px',
                                    color: faq.titleColor,
                                    'font-size': faq.questionSize + 'px',
                                }"
                                type="text"
                                class="block w-full text-base py-2 sm:py-2.5 !pl-10 pr-3 ring-1 ring-inset focus:ring-2 focus:ring-inset border-0 outline-none"
                                :placeholder="$t('shared.search')"
                                v-model="queryText"
                            />
                            <MagnifyingGlassIcon
                                class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 opacity-80"
                            />
                        </div>
                    </div>
                    <SliderLayout
                        v-if="faq.layout === 'slider' && !isLoading"
                        :faq="faq"
                        :questions="questionsToShow"
                        :query="queryText"
                    />
                    <BoardLayout
                        v-if="faq.layout === 'board'"
                        :faq="faq"
                        :sections="sectionsToShow"
                        :query="queryText"
                    />
                    <ListLayout
                        v-if="faq.layout === 'list' || faq.layout === 'aside'"
                        :faq="faq"
                        :sections="sectionsToShow"
                        :query="queryText"
                        @adjustIframe="$emit('adjustIframe')"
                    />
                    <TableLayout
                        v-if="faq.layout === 'table'"
                        :faq="faq"
                        :sections="sectionsToShow"
                        :query="queryText"
                    />
                    <ColumnsLayout
                        v-if="faq.layout === 'columns'"
                        :faq="faq"
                        :sections="sectionsToShow"
                        :query="queryText"
                    />
                </div>
                <div
                    class="order-1 lg:order-2 flex-shrink-0 w-full lg:w-80"
                    v-if="faq.withSections && faq.showNav && faq.layout !== 'slider'"
                >
                    <div
                        class="lg:p-6 sticky top-6"
                        :class="[
                            faq.textAlign === 'right'
                                ? 'text-right'
                                : faq.textAlign === 'center'
                                  ? 'text-center'
                                  : 'text-left',
                        ]"
                    >
                        <div
                            v-if="faq.showSearch && faq.layout !== 'slider'"
                            class="flex justify-end"
                        >
                            <div
                                class="w-full lg:max-w-sm relative mb-8"
                                :style="{
                                    color: faq.titleColor,
                                }"
                            >
                                <input
                                    :style="{
                                        'background-color': faq.faqColor,
                                        '--tw-ring-color': faq.borderColor,
                                        'border-radius': faq.borderRadius + 'px',
                                        color: faq.titleColor,
                                        'font-size': faq.questionSize + 'px',
                                        'placeholder-color': faq.titleColor,
                                    }"
                                    data-js-faq-search
                                    type="text"
                                    class="block w-full text-base py-2 sm:py-2.5 !pl-10 pr-3 ring-1 ring-inset focus:ring-2 focus:ring-inset border-0 outline-none"
                                    :placeholder="$t('shared.search')"
                                    v-model="queryText"
                                />
                                <MagnifyingGlassIcon
                                    class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 opacity-80"
                                />
                            </div>
                        </div>

                        <div class="mb-4">
                            <cUtilsEdit @edit="goToLayoutField('faqNavTitle')">
                                <h4
                                    class="font-bold"
                                    :style="{
                                        color: faq.titleColor,
                                        'font-size': faq.questionSize + 'px',
                                    }"
                                >
                                    {{ faq.navTitle }}
                                </h4>
                            </cUtilsEdit>
                        </div>
                        <div class="sticky top-1 lg:hidden">
                            <cSelect
                                :options="sectionsSelect"
                                :value="currentSection"
                                @update:modelValue="selectSection($event)"
                                :bgColor="faq.faqColor"
                                :textColor="faq.titleColor"
                                :borderColor="faq.borderColor"
                                :borderRadius="faq.borderRadius"
                                :searchEnabled="false"
                            />
                        </div>
                        <ul class="sticky top-1 hidden lg:block">
                            <li
                                :style="{
                                    color: faq.titleColor,
                                    'font-size': faq.questionSize + 'px',
                                }"
                                class="hover:font-semibold mb-1 opacity-80"
                                v-for="section in notDeletedSections"
                                :key="section.id"
                            >
                                <p @click="goTo(section.name)" class="cursor-pointer block py-2">
                                    {{ section.name }}
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="!hideLogo && faq.showFooter"
            class="w-full mt-12 space-x-3 flex items-center justify-center"
        >
            <EasyLogoIcon class="flex-shrink-0 text-faq-500 h-8 w-8" />
            <a
                href="https://easy.tools/faq"
                @click.prevent="goToLink('https://easy.tools/faq')"
                target="_blank"
                class="text-sm"
                :style="{ color: faq.titleColor }"
            >
                {{ $t("r_shared.made_with") }}
                <span class="underline">Easyfaq</span>
            </a>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { isObjectNotEmpty } from "@/shared/utils/helpers";
import BoardLayout from "./BoardLayout.vue";
import SliderLayout from "./SliderLayout.vue";
import ListLayout from "./ListLayout.vue";
import TableLayout from "./TableLayout.vue";
import ColumnsLayout from "./ColumnsLayout.vue";
import SocialsList from "./SocialsList.vue";
import { MagnifyingGlassIcon, EasyLogoIcon } from "@easy/ui/icons";
import cSelect from "@/views/shared/cSelect.vue";
import { handleActionClick } from "@/shared/utils";
import cUtilsEdit from "@/views/shared/cUtilsEdit.vue";
import { useFaqGoTo } from "@/views/tools/faq/utils/useFaqGoTo";
import { useGoToLink } from "@/shared/utils/router/useGoToLink";
import { richTextModel } from "@easy/ui/forms";

export default {
    name: "FaqBody",
    components: {
        BoardLayout,
        SliderLayout,
        ListLayout,
        TableLayout,
        ColumnsLayout,
        SocialsList,
        MagnifyingGlassIcon,
        EasyLogoIcon,
        cSelect,
        cUtilsEdit,
    },
    setup() {
        const { goToLink } = useGoToLink();
        const { goToLayoutField } = useFaqGoTo();
        return { goToLink, goToLayoutField };
    },
    emits: ["adjustIframe"],
    props: {
        faq: Object,
        hideLogo: Boolean,
    },
    data() {
        return {
            currentSection: this.faq.sections?.[0]?.id,
            queryText: "",
            isLoading: false,
        };
    },
    computed: {
        ...mapState("shared/view", ["preview"]),
        faqSubtitle() {
            return richTextModel.parseToHTMLFromMarkdown(this.faq.subtitle);
        },
        notDeletedSections() {
            return this.faq.sections.filter((sec) => !sec._deleted);
        },
        hasSocials() {
            return isObjectNotEmpty(this.faq.socials);
        },
        sectionsSelect() {
            return this.faq.sections.map((sec) => ({
                value: sec.id,
                label: sec.name,
            }));
        },
        sectionsToShow() {
            const sections = this.faq.sections.filter((sec) => !sec._deleted);
            if (this.queryText) {
                return sections.filter((section) => {
                    return section.questions.some((item) => {
                        return (
                            item.question.toLowerCase().includes(this.queryText.toLowerCase()) ||
                            item.answer.toLowerCase().includes(this.queryText.toLowerCase())
                        );
                    });
                });
            }
            return sections;
        },
        notHidedQuestions() {
            const questions = [];
            this.faq.sections.forEach((section) => {
                questions.push(...section.questions.filter((x) => x.hide === false && !x._deleted));
            });
            return questions;
        },
        questionsToShow() {
            if (this.queryText) {
                const query = this.queryText.toLowerCase();
                return this.notHidedQuestions.filter(
                    (item) =>
                        item.question.toLowerCase().includes(query) ||
                        item.answer.toLowerCase().includes(query)
                );
            }
            return this.notHidedQuestions;
        },
        questionsLength() {
            return this.questionsToShow.length;
        },
    },
    watch: {
        // TODO: @Jabi to check if it's needed (it breaks the preview of the Faq project render).
        /*async questionsLength() {
            this.isLoading = true;
            // await this.$nextTick();
            this.isLoading = false;
            //await this.$nextTick();
            // this.$emit("adjustIframe");
        },*/
    },
    methods: {
        goTo(name) {
            const secName = name ? "sec-" + name.replace(" ", "-") : "";
            const targetSection = document.getElementById(secName);
            if (targetSection) {
                targetSection.scrollIntoView({ behavior: "smooth" });
            }
        },
        selectSection(sectionId) {
            this.currentSection = sectionId;
            const section = this.faq.sections.find((sec) => sec.id === sectionId);
            this.goTo(section.name);
        },
        checkPreview() {
            if (this.preview) {
                handleActionClick();
            }
        },
    },
};
</script>

<style scoped>
input::placeholder {
    color: v-bind("faq.titleColor");
    opacity: 0.8;
}
</style>
